import { Interactor } from '@updater-core/lib/store/types';

type RouterState = {
  query: URLSearchParams;
  currentResolvePath: Function;
  previousPathname?: string;
  routeCount?: number;
  pathname: string;
};

export interface RouterInteractor extends Interactor<RouterState> {
  go: (targetPath: string, ...rest) => Promise<void>;
  replace: (targetPath: string) => Promise<void>;
  currentPath: (...rest) => string | null;
  getInitialPath: () => string | null;
  targetPath: () => string | null;
  selectors: {
    query: () => URLSearchParams;
    subdomain: () => string;
    currentPath: () => string;
    isInitialLoad: () => boolean;
  };
}

function createRouterInteractorFacade() {
  let internalRouter;

  function warnIfNotInited(name, fn) {
    return (...args) => {
      if (!internalRouter) {
        // eslint-disable-next-line no-console
        console.error(`Cannot call method ${name}, router not initialized`, fn);
      }
      return fn(...args);
    };
  }

  function decorateWithWarnings(obj, exclude = '') {
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'function' && key !== exclude) {
        // eslint-disable-next-line no-param-reassign
        obj[key] = warnIfNotInited(key, obj[key]);
      }
    });
  }

  let targetPath;

  function handleTargetPath(_targetPath) {
    if (_targetPath === undefined) {
      throw new Error('targetPath is undefined');
    }
    // console.log('GOING', targetPath);
    if (_targetPath[0] !== '/') {
      throw new Error('Target paths must be absolute');
    }
    targetPath = _targetPath;
  }

  const routerInterface = {
    getState: () => internalRouter.getState?.(),
    connect: (...args) => internalRouter.connect(...args),
    subscribe: (...args) => internalRouter.subscribe(...args),
    go: (...args) => {
      const [_targetPath] = args;
      handleTargetPath(_targetPath);
      // console.warn('Use router.actions.go');
      return internalRouter.go(...args);
    },
    replace: async (...args) => {
      if (internalRouter.replace) {
        await internalRouter.replace(...args);
      } else {
        // eslint-disable-next-line no-console
        console.warn('Router.replace not provided. Using Router.go');
        await internalRouter.go(...args);
      }
    },
    currentPath: (...args) =>
      // console.warn('Use router.selectors.currentPath');
      internalRouter.currentPath(...args),
    getInitialPath: () => internalRouter.getInitialPath(),
    targetPath: () => targetPath,
    actions: {
      init: ({ router }) => {
        internalRouter = router;
        // Set the initial "targetPath" to be currentPath on page load
        // in the case of Next/isomorphic router we want to connect
        // otherwise if there is no connect function we can just grab currentPath asap
        if (router.connect) {
          router.connect((_, done) => {
            targetPath = router.currentPath();
            if (targetPath) {
              done();
            }
          });
        } else {
          targetPath = router.currentPath();
        }
      },
      back: (...args) => internalRouter.actions.back(...args),
      go: (...args) => {
        const [_targetPath] = args;
        handleTargetPath(_targetPath);
        internalRouter.actions.go(...args);
      },
      goToSubdomain: (...args) => internalRouter.actions.goToSubdomain(...args),
      goToMoverAppRoute: (...args) =>
        internalRouter.actions.goToMoverAppRoute(...args),
    },
    selectors: {
      query: (...args) => internalRouter.selectors.query(...args),
      subdomain: (...args) => internalRouter.selectors.subdomain(...args),
      currentPath: (...args) => internalRouter.selectors.currentPath(...args),
      isInitialLoad: (...args) =>
        internalRouter.selectors.isInitialLoad(...args),
    },
    destroy: (...args) => internalRouter.destroy(...args),
  } as RouterInteractor;

  decorateWithWarnings(routerInterface);
  decorateWithWarnings(routerInterface.actions, 'init');
  decorateWithWarnings(routerInterface.selectors);

  return routerInterface;
}

export default createRouterInteractorFacade();
